import { Controller } from "@hotwired/stimulus"
import { useStore } from "stimulus-store"
import { allLocalesStore } from "../../stores/locales/all_locales_store"
import { mergeLocaleHierarchy } from "../../stores/locales/merge_locale_hierarchy"
import { WILDCARD } from "../../lib/constants/permissions_constants"
import { removeLocaleWildcardSuffix } from "../../lib/shared/locales/locale_id_helpers"

/**
 * FacilityTreeController manages the hierarchical location tree in the permissions modal
 *
 * Features:
 * - Renders location hierarchy from cached allLocalesStore data
 * - Progressive loading with fallback if store is empty
 * - Search functionality with debouncing
 * - Checkbox selection with wildcard support
 * - Expand/collapse tree nodes
 *
 * Connects to data-controller="trees--facility-tree"
 */
export default class extends Controller {

    static stores = [
        allLocalesStore
    ]

    static targets = [
        "treeContainer",
        "searchInput",
        "searchClearButton",
        "loadingIndicator",
        "errorMessage"
    ]

    static values = {
        userId: String,
        currentSelections: Array  // Pre-selected locations from user policies
    }

    // Lifecycle methods
    connect() {
        useStore(this)

        // Initialize state
        this.expandedNodes = new Set()
        this.selectedLocations = new Set(this.currentSelectionsValue || [])
        this.searchQuery = ''
        this.searchDebounceTimer = null
        // True when a seeded selection arrived before the locale store was ready to render it.
        // The store loads asynchronously on page load, so a seed (e.g. the edit-project modal's
        // describe response) frequently lands while regionsLoaded is still false. We stash this
        // intent and honor it from onAllLocalesStoreUpdate once regions arrive, so the seeded
        // selections render as checked even though the seed itself could not render immediately.
        this._pendingSelectionRender = false

        // Subscribe to store updates
        this.storeUpdateHandler = this.onAllLocalesStoreUpdate.bind(this)

        // Listen for modal open event
        this.boundHandleModalOpen = this.handleModalOpen.bind(this)
        window.addEventListener('forms--permissions-form:opened', this.boundHandleModalOpen)

        // Render tree from cached data. When seeded with selections (e.g. the edit-project
        // modal pre-selecting a project's assigned facilities), eager-render the branches that
        // hold those selections so deep, collapsed selections show checked without the user
        // expanding anything. When the store is not ready yet, applySeededSelections marks the
        // render pending so onAllLocalesStoreUpdate completes it once regions arrive.
        if (this.selectedLocations.size > 0) {
            this.applySeededSelections()
        } else {
            this.renderTreeFromStore()
        }
    }

    disconnect() {
        // Cleanup
        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer)
        }

        // Remove event listener
        window.removeEventListener('forms--permissions-form:opened', this.boundHandleModalOpen)
    }

    // Value change callback
    currentSelectionsValueChanged(newValue) {
        // Update internal state when value changes from parent controller
        this.selectedLocations = new Set(newValue || [])

        // Render the seeded selections. If the store has regions, do it now; otherwise mark it
        // pending so onAllLocalesStoreUpdate renders it the moment regions arrive (the seed
        // commonly lands before the async locale load completes — see _pendingSelectionRender).
        this.applySeededSelections()
    }

    /**
     * Render the current selection set, eager-expanding the branches that hold selections so
     * their (possibly deeply nested) checkboxes are emitted into the DOM and show checked.
     *
     * The locale hierarchy loads asynchronously, so a seed can arrive before the store is ready.
     * When regions are not yet loaded we cannot render anything meaningful, so we record a
     * pending-render intent; onAllLocalesStoreUpdate honors it as soon as regions arrive. This
     * is what makes seeded selections (even top-level regions) show checked despite the race —
     * previously the render was silently skipped when regionsLoaded was false and never retried.
     */
    applySeededSelections() {
        const storeData = this.allLocalesStoreValue

        if (storeData && storeData.regionsLoaded) {
            this._pendingSelectionRender = false
            // ensureSelectedPathsRendered expands ancestors of every selection and fetches any
            // missing hierarchy depth on demand before rendering.
            this.ensureSelectedPathsRendered()
        } else {
            // Store not ready yet — remember to render once it is.
            this._pendingSelectionRender = true
        }
    }

    /**
     * Collapse the tree back to its minimized state, discarding exploration carried over from a
     * previous use (expandedNodes lives for the life of the controller, and a modal that is only
     * hidden/shown never reconnects it, so manual expansions would otherwise persist).
     *
     * Branches holding the CURRENT selections are deliberately re-revealed afterwards via
     * applySeededSelections: buildNodeHTML only emits a child's checkbox when its parent is
     * expanded, so a seeded building/area/room facility would have no visible checkmark if the
     * tree were left strictly fully collapsed.
     *
     * Any in-progress search is cleared too — renderTreeFromStore always draws the UNFILTERED
     * tree, so leaving a stale query in the search box would make it disagree with what is shown.
     */
    collapseAll() {
        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer)
            this.searchDebounceTimer = null
        }

        this.searchQuery = ''
        if (this.hasSearchInputTarget) {
            this.searchInputTarget.value = ''
        }
        if (this.hasSearchClearButtonTarget) {
            this.searchClearButtonTarget.classList.add('d-none')
        }

        // Reassign rather than .clear() so this is safe even if called before connect().
        this.expandedNodes = new Set()

        // Renders now when the store is ready, or defers to the next store update when it is not.
        this.applySeededSelections()
    }

    /**
     * Expand the ancestor chain of every selected path so the branches holding selections are
     * rendered (and therefore show as checked) without the user manually expanding anything.
     *
     * buildNodeHTML only emits a child node's checkbox when its parent isExpanded, so a seeded
     * selection deeper than the top level has no checkbox in the DOM until its ancestors are
     * expanded. This mirrors the search path, which auto-expands ancestors of matches.
     *
     * Only ancestors are expanded, never the selected node itself: selecting an area opens
     * region + building so the area's checkbox renders, but the area is not force-expanded to
     * reveal its rooms. Wildcard paths ("region!building!*") are normalized to their covering
     * node ("region!building") first; a bare global wildcard ("*") has no ancestors to expand.
     */
    expandAncestorsOfSelections() {
        const types = ['region', 'building', 'area', 'room']

        this.selectedLocations.forEach((selectedPath) => {
            let path = selectedPath

            if (path === WILDCARD) {
                return
            }
            path = removeLocaleWildcardSuffix(path)

            // For "regionId!buildingId!areaId" expand "region-regionId" and "building-buildingId"
            // (every ancestor) but NOT the leaf node itself.
            const ids = path.split('!')
            for (let i = 0; i < ids.length - 1; i++) {
                this.expandedNodes.add(`${ types[i] }-${ ids[i] }`)
            }
        })
    }

    /**
     * Eager-render the branches that hold the current selections: expand each selection's
     * ancestors and render SYNCHRONOUSLY against whatever hierarchy depth is already in the
     * store. Any ancestor whose children are not loaded yet is fetched separately and
     * fire-and-forget (fetchMissingSelectionDepth) — those fetches write the store and trigger
     * onAllLocalesStoreUpdate, which re-expands and re-renders when they land.
     *
     * Keeping the render synchronous is deliberate: this method is invoked from several entry
     * points (connect, the seed callback, modal-open, and store updates) that can overlap. If
     * the render awaited the on-demand fetches, a later-starting call could finish its render
     * BEFORE an earlier awaited one, letting a stale selection snapshot win and blank out the
     * checkboxes. A synchronous expand+render means the last call always reflects the current
     * selectedLocations; the async depth-fetch only ever ADDS nodes and re-renders afterward.
     */
    ensureSelectedPathsRendered() {
        // Rebuild expandedNodes to EXACTLY the current selection's ancestors (reset, not add).
        // This is the seed/open path, and it is the authoritative point at which the tree's
        // expansion is defined by the project being opened. Resetting here is what collapses
        // branches that belong to a PREVIOUSLY opened project: collapseAll() runs during open
        // while selectedLocations may still be stale, and the later async seed
        // (currentSelectionsValueChanged) lands here with the correct selection — so clearing
        // first guarantees a region expanded for project A does not linger when project B, which
        // does not use it, is opened. (The additive expandAncestorsOfSelections path in
        // onAllLocalesStoreUpdate must stay additive so a user's manual expansion is preserved as
        // deeper hierarchy levels stream in; only this seed path resets.)
        this.expandedNodes = new Set()
        this.expandAncestorsOfSelections()
        this.renderTreeFromStore()
        this.fetchMissingSelectionDepth()
    }

    /**
     * Fetch, on demand, any ancestor level of a selected path whose children are not yet in the
     * store (the hierarchy loads progressively: regions first, then buildings → areas → rooms
     * in the background). Each successful fetch writes the store via loadChildrenOnDemand, which
     * triggers onAllLocalesStoreUpdate → re-expand + re-render, so the selected branch fills in
     * without waiting for the whole background crawl. Fire-and-forget by design (not awaited by
     * callers) so it never serializes or races the synchronous render above.
     */
    async fetchMissingSelectionDepth() {
        const types = ['region', 'building', 'area', 'room']

        for (const selectedPath of this.selectedLocations) {
            let path = selectedPath

            if (path === WILDCARD) {
                continue
            }
            path = removeLocaleWildcardSuffix(path)

            const ids = path.split('!')
            // Walk each ancestor level and fetch its children if they are not loaded yet.
            for (let i = 0; i < ids.length - 1; i++) {
                const ancestorType = types[i]
                const ancestorNodeId = `${ ancestorType }-${ ids[i] }`
                // The ancestor's full '!'-joined path (region!building!...) up to and including
                // this level — required by getChildrenEndpoint to emit every ancestor param.
                const ancestorPath = ids.slice(0, i + 1).join('!')
                const regions = this.allLocalesStoreValue.locales?.regions || []
                const node = this.findNodeInHierarchy(regions, ancestorNodeId, ancestorType)

                if (node && this.getChildren(node, ancestorType).length === 0) {
                    await this.loadChildrenOnDemand(ancestorNodeId, ancestorType, ancestorPath)
                }
            }
        }
    }

    // Event Handlers
    handleModalOpen(event) {
        const { userId, currentPolicies } = event.detail

        // Update user ID
        this.userIdValue = userId

        // Update current selections from policies
        if (currentPolicies && currentPolicies.locales) {
            this.selectedLocations = new Set(currentPolicies.locales)
            this.currentSelectionsValue = currentPolicies.locales
        }

        // Re-render tree with updated selections. Eager-render the branches holding selections
        // so deep, collapsed selections show checked without manual expansion (same reason as
        // the edit-project seeding path above). Routes through applySeededSelections so a seed
        // arriving before the store is ready is rendered once regions load.
        if (this.selectedLocations.size > 0) {
            this.applySeededSelections()
        } else {
            this.renderTreeFromStore()
        }
    }

    // Store Integration
    onAllLocalesStoreUpdate(newValue) {
        // Re-render tree when store updates (either regions loaded or full hierarchy complete).
        if (!newValue.regionsLoaded) {
            return
        }

        if (this._pendingSelectionRender) {
            // A seed arrived before the store was ready; now that regions exist, render it
            // (expanding ancestors and fetching any missing depth). This is the fix for the
            // race where a seeded selection never rendered because regionsLoaded was false at
            // seed time and no later store write forced a re-render.
            this._pendingSelectionRender = false
            this.ensureSelectedPathsRendered()
            return
        }

        if (this.selectedLocations.size > 0) {
            // As deeper levels stream in from the background load, keep the branches that hold
            // seeded selections expanded so they become visible (checked) as soon as their
            // nodes exist in the store.
            this.expandAncestorsOfSelections()
        }
        this.renderTreeFromStore()
    }

    // Rendering Methods
    renderTreeFromStore() {
        const storeData = this.allLocalesStoreValue

        // Safety check: if store is undefined, show loading
        if (!storeData) {
            this.showLoadingIndicator()
            return
        }

        if (!storeData.regionsLoaded) {
            // Regions not loaded yet - show loading indicator
            // The all_locales_supplier_controller will populate it
            this.showLoadingIndicator()
            return
        }

        this.hideLoadingIndicator()
        this.hideError()

        const regions = storeData.locales?.regions || []
        this.renderTree(regions)
    }

    // Progressive Loading Fallback Methods
    async loadRegions() {
        // Fallback: Load regions if store is empty
        // This should rarely be needed since all_locales_supplier loads on page load
        try {
            this.showLoadingIndicator()

            const regions = []
            let paginationToken = null

            // Fetch all pages of regions
            do {
                const url = '/api/locales/regions/list' + (paginationToken ? `?pagination_token=${ encodeURIComponent(paginationToken) }` : '')

                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    },
                    credentials: 'same-origin',
                    signal: AbortSignal.timeout(30000)  // 30 second timeout
                })

                if (!response.ok) {
                    this.handleApiError(response.status)
                    return
                }

                const data = await response.json()

                // Handle malformed response
                if (!data || typeof data !== 'object') {
                    throw new Error('Malformed API response')
                }

                // Add regions from this page
                if (data.regions) {
                    regions.push(...data.regions.map(region => ({
                        id: region.id,
                        name: region.text || region.name,
                        locale: 'region',
                        buildings: []
                    })))
                }

                // Update pagination token
                paginationToken = data.pagination_token
            } while (paginationToken != null)

            // Handle empty result set
            if (regions.length === 0) {
                this.hideLoadingIndicator()
                this.treeContainerTarget.innerHTML = '<p class="text-muted">No locations available</p>'
                return
            }

            // Update store with all regions
            const currentStore = this.allLocalesStoreValue
            this.setAllLocalesStoreValue(mergeLocaleHierarchy(currentStore, {
                ...currentStore,
                locales: {
                    regions: regions
                }
            }))

            this.hideLoadingIndicator()
            this.renderTree(regions)

        } catch (error) {
            console.error('Error loading regions:', {
                error: error.message,
                name: error.name,
                timestamp: new Date().toISOString()
            })
            this.hideLoadingIndicator()

            // Handle specific error types
            if (error.name === 'TimeoutError' || error.name === 'AbortError') {
                this.showErrorWithRetry('Request timed out. Please try again.', () => this.loadRegions())
            } else if (error.message === 'Failed to fetch' || error.message.includes('NetworkError')) {
                this.showErrorWithRetry('Unable to connect. Please check your connection and try again.', () => this.loadRegions())
            } else if (error.message === 'Malformed API response') {
                this.showErrorWithRetry('Received invalid data from server. Please try again.', () => this.loadRegions())
            } else {
                this.showErrorWithRetry('Unable to load locations. Please try again.', () => this.loadRegions())
            }
        }
    }

    async loadAllLocalesInBackground() {
        // Background loading is handled by all_locales_supplier_controller
        // This method is kept for compatibility but not actively used
    }

    async loadChildrenOnDemand(nodeId, nodeType, nodePath) {
        // On-demand loading for when user expands before background load completes

        try {
            const storeData = this.allLocalesStoreValue
            const regions = storeData.locales?.regions || []

            // Find the node in the store
            const node = this.findNodeInHierarchy(regions, nodeId, nodeType)
            if (!node) {
                console.warn(`Node not found: ${ nodeId }`)
                return
            }

            const childType = this.getChildType(nodeType)
            if (!childType) {
                return
            }

            // Check if children already loaded
            const children = this.getChildren(node, nodeType)
            if (children && children.length > 0) {
                return
            }

            // Fetch children from API with pagination
            const childrenData = await this.fetchChildrenForNode(node, nodeType, nodePath)

            // Handle empty children (no items in this location)
            if (childrenData.length === 0) {
                // This is not an error - some locations are genuinely empty. If this node was
                // previously marked as a FAILED fetch, clear that marker now: the fetch
                // succeeded and returned zero children, so the branch is genuinely empty, not
                // failed. Leaving the flag set would keep the chevron and re-fetch on every
                // expand. clearNodeChildrenLoadFailed re-renders only if it actually changed.
                this.clearNodeChildrenLoadFailed(regions, nodeId, nodeType)
                return
            }

            // Update node with children in store
            this.updateNodeChildrenInStore(regions, nodeId, nodeType, childrenData)

            // Re-render tree to show new children
            this.renderTreeFromStore()

        } catch (error) {
            console.error(`Error loading children for ${ nodeType } ${ nodeId }:`, {
                error: error.message,
                name: error.name,
                timestamp: new Date().toISOString()
            })

            // Handle specific error types
            if (error.name === 'TimeoutError' || error.name === 'AbortError') {
                this.showErrorWithRetry('Request timed out. Please try again.', () => this.loadChildrenOnDemand(nodeId, nodeType, nodePath))
            } else if (error.message === 'Failed to fetch' || error.message.includes('NetworkError')) {
                this.showErrorWithRetry('Unable to connect. Please check your connection and try again.', () => this.loadChildrenOnDemand(nodeId, nodeType, nodePath))
            } else if (error.message === 'Malformed API response') {
                this.showErrorWithRetry('Received invalid data from server. Please try again.', () => this.loadChildrenOnDemand(nodeId, nodeType, nodePath))
            } else {
                this.showErrorWithRetry('Unable to load child locations. Please try again.', () => this.loadChildrenOnDemand(nodeId, nodeType, nodePath))
            }
        }
    }

    async fetchChildrenForNode(node, nodeType, nodePath) {
        const childrenData = []
        let paginationToken = null
        const endpoint = this.getChildrenEndpoint(nodeType, nodePath)

        // A null endpoint means the path was malformed (missing ancestors) or the node type has
        // no children. THROW rather than return [] — an empty array would make
        // loadChildrenOnDemand treat the branch as genuinely empty and clear its
        // childrenLoadFailed marker, permanently hiding the problem. Throwing routes it into the
        // existing catch, which shows the retry UI and keeps the branch recoverable.
        if (!endpoint) {
            throw new Error(`Cannot build children endpoint for ${ nodeType } (path: ${ nodePath })`)
        }

        do {
            const url = endpoint + (paginationToken ? `&pagination_token=${ encodeURIComponent(paginationToken) }` : '')

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                credentials: 'same-origin',
                signal: AbortSignal.timeout(30000)  // 30 second timeout
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${ response.status }`)
            }

            const data = await response.json()

            // Handle malformed response
            if (!data || typeof data !== 'object') {
                throw new Error('Malformed API response')
            }

            // Add children from this page
            const childrenKey = this.getChildrenKey(nodeType)
            if (data[childrenKey]) {
                childrenData.push(...data[childrenKey])
            }

            // Update pagination token
            paginationToken = data.pagination_token
        } while (paginationToken != null)

        return childrenData
    }

    findNodeInHierarchy(regions, nodeId, nodeType) {
        // Find node in the hierarchy
        const searchInNodes = (nodes, type) => {
            for (const node of nodes) {
                if (`${ type }-${ node.id }` === nodeId) {
                    return node
                }

                // Search in children
                const children = this.getChildren(node, type)
                if (children && children.length > 0) {
                    const childType = this.getChildType(type)
                    const found = searchInNodes(children, childType)
                    if (found) {
                        return found
                    }
                }
            }
            return null
        }

        return searchInNodes(regions, 'region')
    }

    // Clears the childrenLoadFailed marker from a node after a successful fetch that returned
    // ZERO children — i.e. the branch is genuinely empty, not failed. Only writes the store (and
    // re-renders) if the node was actually marked failed, so a normal empty node is a no-op.
    clearNodeChildrenLoadFailed(regions, nodeId, nodeType) {
        const node = this.findNodeInHierarchy(regions, nodeId, nodeType)
        if (!node || !node.childrenLoadFailed) {
            return
        }

        const updatedRegions = JSON.parse(JSON.stringify(regions))
        const clearFlag = (nodes, type) => {
            for (const current of nodes) {
                if (`${ type }-${ current.id }` === nodeId) {
                    delete current.childrenLoadFailed
                    return true
                }
                const children = this.getChildren(current, type)
                if (children && children.length > 0) {
                    if (clearFlag(children, this.getChildType(type))) {
                        return true
                    }
                }
            }
            return false
        }
        clearFlag(updatedRegions, 'region')

        const storeData = this.allLocalesStoreValue
        this.setAllLocalesStoreValue(mergeLocaleHierarchy(storeData, {
            ...storeData,
            locales: {
                regions: updatedRegions
            }
        }))
        this.renderTreeFromStore()
    }

    updateNodeChildrenInStore(regions, nodeId, nodeType, childrenData) {
        // Deep clone the regions to avoid mutation
        const updatedRegions = JSON.parse(JSON.stringify(regions))

        // Find and update the node
        const updateNode = (nodes, type) => {
            for (const node of nodes) {
                if (`${ type }-${ node.id }` === nodeId) {
                    // Found the node - add children
                    const childType = this.getChildType(type)
                    const childrenKey = this.getChildrenKey(type)

                    node[childrenKey] = childrenData.map(child => ({
                        id: child.id,
                        name: child.name,
                        locale: childType,
                        ...(childType !== 'room' ? { [this.getChildrenKey(childType)]: [] } : {})
                    }))
                    // Children were successfully fetched and written, so this node is no longer
                    // in the FAILED state — clear the marker so it stops being treated as a
                    // retryable (failed) branch.
                    delete node.childrenLoadFailed
                    return true
                }

                // Search in children
                const children = this.getChildren(node, type)
                if (children && children.length > 0) {
                    const childType = this.getChildType(type)
                    if (updateNode(children, childType)) {
                        return true
                    }
                }
            }
            return false
        }

        updateNode(updatedRegions, 'region')

        // Update store with modified hierarchy
        const storeData = this.allLocalesStoreValue
        this.setAllLocalesStoreValue(mergeLocaleHierarchy(storeData, {
            ...storeData,
            locales: {
                regions: updatedRegions
            }
        }))
    }

    getChildrenKey(nodeType) {
        switch (nodeType) {
            case 'region':
                return 'buildings'
            case 'building':
                return 'areas'
            case 'area':
                return 'rooms'
            default:
                return null
        }
    }

    // Builds the child-list URL for a node from its FULL '!'-joined ancestor path (not just its
    // own id). Every ancestor is required because the server stores Locale#locale as the full
    // '!'-joined path (region!building!area...) and filters on that exact prefix — it cannot
    // derive missing ancestors. Omitting region when expanding a building previously left the
    // server's locale_id nil, which it then concatenated onto ("nil + '!<id>'") and surfaced as a
    // 500. We mirror the supplier's own URLs: buildings/list?region=R,
    // areas/list?region=R&building=B, rooms/list?region=R&building=B&area=A.
    getChildrenEndpoint(nodeType, nodePath) {
        const endpoints = {
            region: '/api/locales/buildings/list',
            building: '/api/locales/areas/list',
            area: '/api/locales/rooms/list'
        }
        const endpoint = endpoints[nodeType]
        if (!endpoint) {
            // Any other nodeType (e.g. 'room') has no children to list.
            return null
        }

        // The param name for each successive path segment, in order.
        const paramNames = ['region', 'building', 'area']
        // The node's own depth: region=1, building=2, area=3.
        const expectedDepth = paramNames.indexOf(nodeType) + 1

        const segments = (nodePath || '').split('!').filter(segment => segment.length > 0)
        if (segments.length !== expectedDepth) {
            // Malformed path (wrong number of ancestors): refuse to issue a wrong query.
            return null
        }

        const params = new URLSearchParams()
        segments.forEach((segment, index) => {
            params.append(paramNames[index], segment)
        })

        return `${ endpoint }?${ params }`
    }

    renderTree(regions) {
        if (!this.hasTreeContainerTarget) {
            return
        }

        if (regions.length === 0) {
            this.treeContainerTarget.innerHTML = '<p class="text-muted">No locations available</p>'
            return
        }

        // Precompute descendant-selection counts once for this render so every node can
        // cheaply look up whether it has selected children beneath it (badge display).
        this._descendantSelectionCounts = this.computeDescendantSelectionCounts()

        const sortedRegions = [...regions].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        const treeHTML = sortedRegions.map(region => this.buildNodeHTML(region, 0, 'region')).join('')
        this.treeContainerTarget.innerHTML = `<div role="tree">${ treeHTML }</div>`
    }

    buildNodeHTML(node, level, nodeType, parentPath = '') {
        const nodeId = `${ nodeType }-${ node.id }`
        const nodePath = parentPath ? `${ parentPath }!${ node.id }` : node.id
        const isExpanded = this.expandedNodes.has(nodeId)
        const isSelected = this.selectedLocations.has(nodePath)
        const hasChildren = this.hasChildren(node, nodeType)

        // Check if we should show expand icon even without loaded children
        // (during background loading, nodes might have children that aren't loaded yet)
        const storeData = this.allLocalesStoreValue
        // Show the expand chevron when children might still exist: either the background crawl
        // hasn't finished yet, OR this node's child fetch FAILED (childrenLoadFailed) — a failed
        // branch is NOT genuinely empty, so it must stay expandable for on-demand retry even
        // after initialLoadComplete.
        const mightHaveChildren = (!storeData.initialLoadComplete || node.childrenLoadFailed) && this.canHaveChildren(nodeType)
        const showExpandIcon = hasChildren || mightHaveChildren

        const indentStyle = `padding-left: ${ level * 1.5 }rem;`
        const expandIcon = showExpandIcon
            ? (isExpanded ? 'bi-chevron-down' : 'bi-chevron-right')
            : ''

        let html = `
            <div class="tree-node" 
                 data-node-id="${ nodeId }" 
                 data-node-path="${ nodePath }"
                 data-node-type="${ nodeType }"
                 role="treeitem"
                 aria-level="${ level + 1 }"
                 ${ showExpandIcon ? `aria-expanded="${ isExpanded }"` : '' }
                 style="${ indentStyle }">
                ${ showExpandIcon ? `
                <span class="tree-node-expand" data-action="click->trees--facility-tree#toggleNode">
                    <i class="bi ${ expandIcon }"></i>
                </span>
                ` : '<span class="tree-node-spacer"></span>' }
                <input type="checkbox" 
                       class="tree-node-checkbox"
                       data-node-path="${ nodePath }"
                       ${ isSelected ? 'checked' : '' }
                       data-action="change->trees--facility-tree#toggleLocation">
                <label class="tree-node-label">${ this.escapeHtml(node.name) }${ this.selectionBadgeHtml(nodePath, isSelected) }</label>
            </div>
        `

        // Add children if expanded
        if (hasChildren && isExpanded) {
            const children = this.getChildren(node, nodeType)
            const sortedChildren = [...children].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
            const childType = this.getChildType(nodeType)
            html += sortedChildren.map(child => this.buildNodeHTML(child, level + 1, childType, nodePath)).join('')
        }

        return html
    }

    escapeHtml(text) {
        const div = document.createElement('div')
        div.textContent = text
        return div.innerHTML
    }

    /**
     * Build a map of nodePath -> number of selected paths that live BENEATH that node.
     *
     * Derived purely from this.selectedLocations (which is always complete regardless of
     * which tree nodes are currently rendered/loaded), so it stays correct under lazy /
     * on-demand child loading. For each selected path we increment the count of every
     * ancestor prefix, but NOT the node itself — a node that is directly selected does not
     * count as its own "child selection".
     *
     * Wildcard paths (e.g. "regionId!buildingId!*") are normalized by dropping the trailing
     * "!*" segment so the covering location (buildingId) is credited as the selected
     * descendant of its ancestors, rather than a phantom "*" node.
     *
     * Called once per render and stashed on the instance so both the full-tree and
     * filtered-tree recursions can read it without recomputing per node.
     *
     * @returns {Map<string, number>} nodePath -> count of selected descendants
     */
    computeDescendantSelectionCounts() {
        const counts = new Map()

        this.selectedLocations.forEach(selectedPath => {
            // A bare global wildcard has no ancestor to badge; skip it.
            if (selectedPath === WILDCARD) {
                return
            }

            // Normalize a trailing wildcard segment: "a!b!*" credits "a!b" (and its ancestors).
            const path = removeLocaleWildcardSuffix(selectedPath)

            // Walk each ancestor prefix of this path and increment its count. Stop before the
            // full path itself so a directly-selected node is not counted as its own child.
            const parts = path.split('!')
            for (let i = 1; i < parts.length; i++) {
                const ancestorPath = parts.slice(0, i).join('!')
                counts.set(ancestorPath, (counts.get(ancestorPath) || 0) + 1)
            }
        })

        return counts
    }

    /**
     * Build the badge markup shown on a node whose descendants include selections but which
     * is NOT itself selected — surfacing "hidden" partial selection when a collapsed ancestor
     * isn't checked. Returns an empty string when the node is selected (the checkbox already
     * communicates that) or has no selected descendants.
     *
     * The count is kept compact (a pill) while the full, unambiguous phrasing lives in the
     * title/aria-label for hover + assistive technology.
     *
     * @param {string} nodePath - the '!'-joined path of the node being rendered
     * @param {boolean} isSelected - whether the node itself is selected
     * @returns {string} badge HTML, or '' when no badge should be shown
     */
    selectionBadgeHtml(nodePath, isSelected) {
        if (isSelected) {
            return ''
        }

        const count = this._descendantSelectionCounts
            ? (this._descendantSelectionCounts.get(nodePath) || 0)
            : 0

        if (count <= 0) {
            return ''
        }

        const label = count === 1
            ? '1 child facility is part of this project'
            : `${ count } child facilities are part of this project`

        return `<span class="badge rounded-pill bg-secondary ms-2"
                      title="${ label }"
                      aria-label="${ label }">${ count }</span>`
    }

    highlightMatches(text, query) {
        if (!query) {
            return this.escapeHtml(text)
        }

        const escapedText = this.escapeHtml(text)
        const regex = new RegExp(`(${this.escapeRegex(query)})`, 'gi')
        return escapedText.replace(regex, '<mark class="search-highlight">$1</mark>')
    }

    escapeRegex(string) {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    }

    updateTreeVisibility() {
        // Update visibility based on expand/collapse state
        // This is handled by re-rendering the tree
        this.renderTreeFromStore()
    }

    hasChildren(node, nodeType) {
        switch (nodeType) {
            case 'region':
                return node.buildings && node.buildings.length > 0
            case 'building':
                return node.areas && node.areas.length > 0
            case 'area':
                return node.rooms && node.rooms.length > 0
            case 'room':
                return false
            default:
                return false
        }
    }

    canHaveChildren(nodeType) {
        // Returns true if this node type can potentially have children
        // (used during background loading to show expand icons)
        switch (nodeType) {
            case 'region':
            case 'building':
            case 'area':
                return true
            case 'room':
                return false
            default:
                return false
        }
    }

    getChildren(node, nodeType) {
        switch (nodeType) {
            case 'region':
                return node.buildings || []
            case 'building':
                return node.areas || []
            case 'area':
                return node.rooms || []
            default:
                return []
        }
    }

    getChildType(nodeType) {
        const typeMap = {
            'region': 'building',
            'building': 'area',
            'area': 'room'
        }
        return typeMap[nodeType] || null
    }

    getLocationPath(node, nodeType) {
        // Path is now tracked in data-node-path attribute during rendering
        // This method is kept for compatibility
        return node.id
    }

    // Tree Interaction Methods
    async toggleNode(event) {
        event.preventDefault()
        const nodeElement = event.target.closest('.tree-node')
        if (!nodeElement) {
            return
        }

        const nodeId = nodeElement.dataset.nodeId
        const nodeType = nodeElement.dataset.nodeType
        const nodePath = nodeElement.dataset.nodePath

        if (this.expandedNodes.has(nodeId)) {
            // Collapse node
            this.expandedNodes.delete(nodeId)
        } else {
            // Expand node
            this.expandedNodes.add(nodeId)

            // Check if children need to be loaded on-demand
            const storeData = this.allLocalesStoreValue
            const node = this.findNodeInHierarchy(storeData.locales?.regions || [], nodeId, nodeType)
            // Take the on-demand fetch path when the background crawl is still running OR when
            // this node's earlier child fetch FAILED (childrenLoadFailed). A failed branch is
            // not genuinely empty — once the crawl is done, initialLoadComplete alone would skip
            // the fetch and leave the branch permanently stuck, so the flag re-opens the path.
            if (node && (!storeData.initialLoadComplete || node.childrenLoadFailed)) {
                const children = this.getChildren(node, nodeType)
                if (!children || children.length === 0) {
                    // No children loaded yet (or a prior fetch failed) - show spinner and fetch on demand
                    this.showNodeLoadingSpinner(nodeElement)
                    await this.loadChildrenOnDemand(nodeId, nodeType, nodePath)
                    this.hideNodeLoadingSpinner(nodeElement)
                }
            }
        }

        this.updateTreeVisibility()
    }

    // Checkbox Management
    toggleLocation(event) {
        const checkbox = event.target
        const nodePath = checkbox.dataset.nodePath

        if (!nodePath) {
            return
        }

        if (checkbox.checked) {
            // Remove any ancestor paths (selecting a child supersedes the parent)
            this.uncheckAncestors(nodePath)

            // Remove any already-selected descendant paths (selecting an ancestor supersedes
            // its children — e.g. selecting an area clears any previously-selected rooms under it)
            this.uncheckDescendants(nodePath)

            // Add location to selections
            this.selectedLocations.add(nodePath)
        } else {
            // Remove location from selections
            this.selectedLocations.delete(nodePath)

            // Also uncheck all descendants (cascade)
            this.uncheckDescendants(nodePath)
        }

        // Re-render to reflect ancestor/descendant state changes
        this.renderTreeFromStore()

        // Update the value for external access
        this.currentSelectionsValue = Array.from(this.selectedLocations)

        // Dispatch event for parent controllers to react to selection changes
        this.dispatch('selectionChanged', {
            detail: {
                selections: this.currentSelectionsValue
            }
        })
    }

    uncheckAncestors(childPath) {
        const parts = childPath.split('!')
        // Build each ancestor path and remove if selected
        // e.g., for "a!b!c" → check "a" and "a!b"
        for (let i = 1; i < parts.length; i++) {
            const ancestorPath = parts.slice(0, i).join('!')
            this.selectedLocations.delete(ancestorPath)
        }
    }

    uncheckDescendants(parentPath) {
        // Remove all paths that start with parentPath
        const pathsToRemove = []

        this.selectedLocations.forEach(path => {
            if (path.startsWith(parentPath + '!')) {
                pathsToRemove.push(path)
            }
        })

        pathsToRemove.forEach(path => this.selectedLocations.delete(path))
    }

    toggleLocationWildcard(event) {
        const checkbox = event.target
        const nodePath = checkbox.dataset.nodePath
        const wildcardPath = `${ nodePath }!${ WILDCARD }`

        if (!nodePath) {
            return
        }

        if (checkbox.checked) {
            // Add wildcard to selections
            this.selectedLocations.add(wildcardPath)

            // Remove redundant child paths
            this.removeRedundantPaths(nodePath)

            // Disable descendant checkboxes
            this.disableDescendantCheckboxes(nodePath, true)
        } else {
            // Remove wildcard from selections
            this.selectedLocations.delete(wildcardPath)

            // Re-enable descendant checkboxes
            this.disableDescendantCheckboxes(nodePath, false)
        }

        // Update the value for external access
        this.currentSelectionsValue = Array.from(this.selectedLocations)

        // Re-render to update UI
        this.updateTreeVisibility()
    }

    removeRedundantPaths(parentPath) {
        // Remove all child paths that are covered by the wildcard
        const pathsToRemove = []

        this.selectedLocations.forEach(path => {
            if (path.startsWith(parentPath + '!') && !path.endsWith('!' + WILDCARD)) {
                pathsToRemove.push(path)
            }
        })

        pathsToRemove.forEach(path => this.selectedLocations.delete(path))
    }

    disableDescendantCheckboxes(parentPath, disabled) {
        // Find all checkboxes for descendants and disable/enable them
        if (!this.hasTreeContainerTarget) {
            return
        }

        const checkboxes = this.treeContainerTarget.querySelectorAll('.tree-node-checkbox')

        checkboxes.forEach(checkbox => {
            const path = checkbox.dataset.nodePath
            if (path && path.startsWith(parentPath + '!')) {
                checkbox.disabled = disabled

                // Update visual styling
                const nodeElement = checkbox.closest('.tree-node')
                if (nodeElement) {
                    if (disabled) {
                        nodeElement.classList.add('disabled')
                    } else {
                        nodeElement.classList.remove('disabled')
                    }
                }
            }
        })
    }

    getSelectedLocations() {
        return Array.from(this.selectedLocations)
    }

    // Search Methods
    handleSearchInput(event) {
        const query = event.target.value

        // Clear existing debounce timer
        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer)
        }

        // Debounce search by 300ms
        this.searchDebounceTimer = setTimeout(() => {
            this.performSearch(query)
        }, 300)

        // Show/hide clear button
        if (this.hasSearchClearButtonTarget) {
            if (query.length > 0) {
                this.searchClearButtonTarget.classList.remove('d-none')
            } else {
                this.searchClearButtonTarget.classList.add('d-none')
            }
        }
    }

    clearSearch() {
        // Clear search input
        if (this.hasSearchInputTarget) {
            this.searchInputTarget.value = ''
        }

        // Hide clear button
        if (this.hasSearchClearButtonTarget) {
            this.searchClearButtonTarget.classList.add('d-none')
        }

        // Clear search query and restore tree
        this.searchQuery = ''
        this.performSearch('')
    }

    performSearch(query) {
        this.searchQuery = query.toLowerCase().trim()

        if (this.searchQuery === '') {
            // No search - render full tree
            this.renderTreeFromStore()
        } else {
            // Filter tree to show only matches and ancestors
            this.filterTree(this.searchQuery)
        }
    }

    filterTree(query) {
        const storeData = this.allLocalesStoreValue

        if (!storeData.regionsLoaded) {
            return
        }

        const regions = storeData.locales?.regions || []

        // Find all matching nodes and their ancestors
        const matchingNodes = new Set()
        const ancestorNodes = new Set()

        this.findMatchingNodes(regions, query, 'region', '', matchingNodes, ancestorNodes)

        if (matchingNodes.size === 0) {
            // No matches found
            this.treeContainerTarget.innerHTML = '<p class="text-muted">No locations found</p>'
            return
        }

        // Auto-expand all ancestor nodes
        ancestorNodes.forEach(nodeId => this.expandedNodes.add(nodeId))

        // Render filtered tree
        this.renderFilteredTree(regions, matchingNodes, ancestorNodes)
    }

    findMatchingNodes(nodes, query, nodeType, parentPath, matchingNodes, ancestorNodes) {
        nodes.forEach(node => {
            const nodeId = `${ nodeType }-${ node.id }`
            const nodePath = parentPath ? `${ parentPath }!${ node.id }` : node.id
            const matches = node.name.toLowerCase().includes(query)

            if (matches) {
                matchingNodes.add(nodeId)
                // Add all ancestors
                this.addAncestors(parentPath, nodeType, ancestorNodes)
            }

            // Recursively search children
            const children = this.getChildren(node, nodeType)
            if (children && children.length > 0) {
                const childType = this.getChildType(nodeType)
                const childMatches = this.findMatchingNodes(
                    children,
                    query,
                    childType,
                    nodePath,
                    matchingNodes,
                    ancestorNodes
                )

                // If any child matches, this node is an ancestor
                if (childMatches) {
                    ancestorNodes.add(nodeId)
                }
            }
        })

        return matchingNodes.size > 0
    }

    addAncestors(path, nodeType, ancestorNodes) {
        if (!path) {
            return
        }

        const parts = path.split('!')
        let currentPath = ''
        const types = ['region', 'building', 'area', 'room']
        let typeIndex = types.indexOf(nodeType) - 1

        parts.forEach((id, index) => {
            if (typeIndex >= 0) {
                const ancestorType = types[typeIndex]
                const ancestorId = `${ ancestorType }-${ id }`
                ancestorNodes.add(ancestorId)
                typeIndex--
            }
        })
    }

    renderFilteredTree(regions, matchingNodes, ancestorNodes) {
        if (!this.hasTreeContainerTarget) {
            return
        }

        // Precompute descendant-selection counts once for this render (same as renderTree)
        // so filtered nodes can display the selected-children badge too.
        this._descendantSelectionCounts = this.computeDescendantSelectionCounts()

        const treeHTML = regions
            .filter(region => {
                const nodeId = `region-${ region.id }`
                return matchingNodes.has(nodeId) || ancestorNodes.has(nodeId)
            })
            .map(region => this.buildFilteredNodeHTML(region, 0, 'region', '', matchingNodes, ancestorNodes))
            .join('')

        this.treeContainerTarget.innerHTML = `<div role="tree">${ treeHTML }</div>`
    }

    buildFilteredNodeHTML(node, level, nodeType, parentPath, matchingNodes, ancestorNodes) {
        const nodeId = `${ nodeType }-${ node.id }`
        const nodePath = parentPath ? `${ parentPath }!${ node.id }` : node.id
        const isExpanded = this.expandedNodes.has(nodeId)
        const isSelected = this.selectedLocations.has(nodePath)
        const isMatch = matchingNodes.has(nodeId)
        const isAncestor = ancestorNodes.has(nodeId)

        // Only show nodes that match or are ancestors
        if (!isMatch && !isAncestor) {
            return ''
        }

        const children = this.getChildren(node, nodeType)
        const childType = this.getChildType(nodeType)
        const hasVisibleChildren = children && children.some(child => {
            const childId = `${ childType }-${ child.id }`
            return matchingNodes.has(childId) || ancestorNodes.has(childId)
        })

        const indentStyle = `padding-left: ${ level * 1.5 }rem;`
        const expandIcon = hasVisibleChildren
            ? (isExpanded ? 'bi-chevron-down' : 'bi-chevron-right')
            : ''

        // Highlight matching text
        const displayName = isMatch
            ? this.highlightMatches(node.name, this.searchQuery)
            : this.escapeHtml(node.name)

        let html = `
            <div class="tree-node" 
                 data-node-id="${ nodeId }" 
                 data-node-path="${ nodePath }"
                 data-node-type="${ nodeType }"
                 role="treeitem"
                 aria-level="${ level + 1 }"
                 ${ hasVisibleChildren ? `aria-expanded="${ isExpanded }"` : '' }
                 style="${ indentStyle }">
                ${ hasVisibleChildren ? `
                <span class="tree-node-expand" data-action="click->trees--facility-tree#toggleNode">
                    <i class="bi ${ expandIcon }"></i>
                </span>
                ` : '<span class="tree-node-spacer"></span>' }
                <input type="checkbox" 
                       class="tree-node-checkbox"
                       data-node-path="${ nodePath }"
                       ${ isSelected ? 'checked' : '' }
                       data-action="change->trees--facility-tree#toggleLocation">
                <label class="tree-node-label">${ displayName }${ this.selectionBadgeHtml(nodePath, isSelected) }</label>
            </div>
        `

        // Add children if expanded
        if (hasVisibleChildren && isExpanded) {
            html += children
                .map(child => this.buildFilteredNodeHTML(child, level + 1, childType, nodePath, matchingNodes, ancestorNodes))
                .filter(childHTML => childHTML !== '')
                .join('')
        }

        return html
    }

    // UI Helper Methods
    showLoadingIndicator() {
        if (this.hasLoadingIndicatorTarget) {
            this.loadingIndicatorTarget.classList.remove('d-none')
        }
    }

    hideLoadingIndicator() {
        if (this.hasLoadingIndicatorTarget) {
            this.loadingIndicatorTarget.classList.add('d-none')
        }
    }

    showNodeLoadingSpinner(nodeElement) {
        // Replace the expand icon with a spinner while loading children
        const expandIcon = nodeElement.querySelector('.tree-node-expand i')
        if (expandIcon) {
            // Store original icon classes
            expandIcon.dataset.originalClasses = expandIcon.className
            // Replace with spinner
            expandIcon.className = 'spinner-border spinner-border-sm'
        }
    }

    hideNodeLoadingSpinner(nodeElement) {
        // Restore the original expand icon after loading completes
        const expandIcon = nodeElement.querySelector('.tree-node-expand i')
        if (expandIcon && expandIcon.dataset.originalClasses) {
            // Restore original icon
            expandIcon.className = expandIcon.dataset.originalClasses
            delete expandIcon.dataset.originalClasses
        }
    }

    showError(message) {
        if (this.hasErrorMessageTarget) {
            this.errorMessageTarget.innerHTML = this.escapeHtml(message)
            this.errorMessageTarget.classList.remove('d-none')
        }
    }

    hideError() {
        if (this.hasErrorMessageTarget) {
            this.errorMessageTarget.classList.add('d-none')
            this.errorMessageTarget.innerHTML = ''
        }
    }

    /**
     * Show error message with retry button
     * @param {string} message - User-friendly error message
     * @param {Function} retryCallback - Function to call when retry button is clicked
     */
    showErrorWithRetry(message, retryCallback) {
        if (this.hasErrorMessageTarget) {
            const retryButton = `
                <button type="button" 
                        class="btn btn-sm btn-primary ms-2" 
                        data-action="click->trees--facility-tree#handleRetry">
                    Try Again
                </button>
            `
            this.errorMessageTarget.innerHTML = `
                <div class="d-flex align-items-center justify-content-between">
                    <span>${ this.escapeHtml(message) }</span>
                    ${ retryButton }
                </div>
            `
            this.errorMessageTarget.classList.remove('d-none')

            // Store retry callback for later use
            this.retryCallback = retryCallback
        }
    }

    /**
     * Handle retry button click
     */
    handleRetry(event) {
        event.preventDefault()
        this.hideError()

        if (this.retryCallback && typeof this.retryCallback === 'function') {
            this.retryCallback()
        }
    }

    /**
     * Handle API errors based on status code
     * @param {number} status - HTTP status code
     */
    handleApiError(status) {
        let message = ''

        switch (status) {
            case 401:
            case 403:
                message = "You don't have permission to view this data."
                this.showError(message)
                break
            case 404:
                message = "Requested data not found."
                this.showError(message)
                break
            case 408:
            case 504:
                message = "Request timed out. Please try again."
                this.showErrorWithRetry(message, () => this.loadRegions())
                break
            case 500:
            case 502:
            case 503:
                message = "Server error occurred. Please try again later."
                this.showErrorWithRetry(message, () => this.loadRegions())
                break
            default:
                message = "An error occurred. Please try again."
                this.showErrorWithRetry(message, () => this.loadRegions())
        }

        console.error('API Error:', {
            status,
            message,
            timestamp: new Date().toISOString()
        })
    }
}
